import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

function EyeIcon({ visible }) {
  return visible ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function Register({ onLoginSuccess }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get('plan');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const strongEnough = password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password);
    if (!strongEnough) {
      setError(t('register.passwordRequirementsError'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('register.passwordsDontMatch'));
      return;
    }
    if (!acceptTerms) {
      setError(t('register.termsRequired'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/v1/auth/register`, { email, password, accept_terms: acceptTerms });
      const token = response.data.access_token;
      onLoginSuccess(token);

      if (planFromUrl === 'starter' || planFromUrl === 'pro') {
        try {
          const checkoutResponse = await axios.post(
            `${API_URL}/api/v1/billing/checkout`,
            { plan_type: planFromUrl },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          window.location.href = checkoutResponse.data.url;
          return;
        } catch (checkoutErr) {
          navigate('/billing');
          return;
        }
      }
      navigate('/dashboard');
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setError(t('register.emailTaken'));
      } else if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError(t('register.genericError'));
      }
      setLoading(false);
    }
  };

  const inputClass = 'w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2.5 pr-10 text-white text-sm focus:outline-none focus:border-accent';
  const planName = planFromUrl === 'pro' ? 'Pro' : 'Starter';

  return (
    <div className="bg-panel min-h-screen flex items-center justify-center px-4 font-sans">
      <div className="bg-surface border border-edge rounded-2xl p-8 w-full max-w-sm">
        <h1 className="text-accent text-xl font-extrabold mb-1">{t('common.appName')}</h1>
        <p className="text-muted text-sm mb-6">
          {planFromUrl ? t('register.subtitleWithPlan', { plan: planName }) : t('register.subtitleDefault')}
        </p>

        {error && (
          <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="text-muted text-xs block mb-1.5">{t('login.email')}</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent" />
          </div>
          <div>
            <label className="text-muted text-xs block mb-1.5">{t('login.password')}</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted cursor-pointer"
                tabIndex={-1}
              >
                <EyeIcon visible={showPassword} />
              </button>
            </div>
            <p className="text-muted text-[11px] mt-1.5">{t('register.passwordHint')}</p>
          </div>
          <div>
            <label className="text-muted text-xs block mb-1.5">{t('register.confirmPassword')}</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputClass}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted cursor-pointer"
                tabIndex={-1}
              >
                <EyeIcon visible={showConfirmPassword} />
              </button>
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-muted cursor-pointer">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="mt-0.5 accent-[#c8f135] cursor-pointer"
            />
            <span>
              {t('register.termsPrefix')}{' '}
              <a href="/aviso-legal" target="_blank" rel="noopener noreferrer" className="text-accent">{t('register.termsLegal')}</a>
              {' '}{t('register.termsAnd')}{' '}
              <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-accent">{t('register.termsPrivacy')}</a>
            </span>
          </label>
          <button
            type="submit"
            disabled={loading}
            className={`bg-accent text-panel font-bold text-sm px-4 py-2.5 rounded-lg cursor-pointer ${loading ? 'opacity-70' : ''}`}
          >
            {loading ? t('register.submitting') : t('register.submit')}
          </button>
        </form>

        <p className="text-muted text-xs mt-5 text-center">
          {t('register.haveAccount')}{' '}
          <span className="text-accent cursor-pointer" onClick={() => navigate('/login')}>
            {t('register.loginLink')}
          </span>
        </p>
      </div>
    </div>
  );
}

export default Register;